import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import {
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Spinner,
  Input,
  Button,
  Dropdown,
  Option,
  Text,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowUpRegular,
  ArrowDownRegular,
  SearchRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  ChevronDoubleLeftRegular,
  ChevronDoubleRightRegular,
  ChevronDown12Regular,
  ChevronRight12Regular,
} from '@fluentui/react-icons';
import { useTableState, findFirstMatch, type SortDirection } from '../hooks/useTableState';
import { ROW_LINK_HOST } from './links/realLink';
import { bucketFor, compareGroupKeys, type GroupGranularity } from './listFilters/groupBy';

export interface Column<T> {
  key: keyof T | string;
  header: string;
  sortable?: boolean;
  filterable?: boolean;
  filterType?: 'text' | 'select' | 'date' | 'date-range' | 'number' | 'picklist' | 'boolean';
  filterOptions?: { value: string; label: string }[];
  render?: (item: T) => React.ReactNode;
  width?: string;
  getValue?: (item: T) => string | number | Date | boolean | null | undefined;
}

export type PageSize = 25 | 50 | 100;
export const PAGE_SIZE_OPTIONS: PageSize[] = [25, 50, 100];

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  searchable?: boolean;
  searchPlaceholder?: string;
  searchKeys?: (keyof T)[];
  // Controlled search: when onSearchTextChange is provided, the parent owns the search box (e.g.
  // renders it in a toolbar row) and DataTable does NOT render its own; searchText is the value.
  searchText?: string;
  onSearchTextChange?: (text: string) => void;
  // Controlled sort: when onSortChange is provided, sort is driven by sortColumn/sortDirection
  // (e.g. from the URL) instead of internal state; clicking a header cycles asc → desc → none.
  sortColumn?: keyof T | string | null;
  sortDirection?: SortDirection | null;
  onSortChange?: (next: { column: keyof T | string; direction: SortDirection } | null) => void;
  // Renders extra header content (e.g. a per-column filter funnel) after the label + sort icon.
  renderHeaderExtra?: (columnKey: string) => React.ReactNode;
  onRowClick?: (item: T) => void;
  // Real links (fleet ruling 2026-10-29): when a row navigates to another page, return its href
  // here instead of navigating from onRowClick. DataTable marks the row as a RowLink host (so a
  // right-click anywhere on the row offers "Open in new tab") and stops attaching its own onClick
  // to rows that have one -- render the actual `RowLink` (web-app/src/components/links/EntityLink.tsx)
  // in that row's primary-identity column from the column's own `render`, with the SAME href. Rows
  // for which this returns undefined keep using onRowClick (e.g. one that opens a dialog instead of
  // navigating), so a single DataTable can mix both per row.
  getRowHref?: (item: T) => string | undefined;
  emptyMessage?: string;
  loading?: boolean;
  rowKey: keyof T | ((item: T) => string);
  initialSort?: { column: keyof T | string; direction: SortDirection };
  // Pagination props
  paginated?: boolean;
  pageSize?: PageSize;
  onPageSizeChange?: (size: PageSize) => void;
  // If provided, pagination is controlled externally
  currentPage?: number;
  onPageChange?: (page: number) => void;
  // Returns the items on the current page (for select all on page)
  onPageDataChange?: (items: T[]) => void;
  // Column visibility (optional — when provided, only matching columns are rendered)
  visibleColumns?: string[];
  // Group-by (fleet filter contract v3 Part II §13.3-13.8, optional-conformance -- M3): the
  // grouped column's KEY and the active granularity. Controlled, like sort -- the PAGE decides
  // which columns offer grouping at all (via its own ColumnMenu wiring in renderHeaderExtra); this
  // prop pair is just "what's active right now". Pagination is disabled while grouped (every row
  // renders, organized into sections) -- the contract does not address the pagination/grouping
  // interaction, and showing a group cut off mid-page by a page boundary would be worse than
  // showing everything grouped, for the row counts most apps that adopt this will actually have.
  groupBy?: string | null;
  groupGranularity?: GroupGranularity;
}

function getRowKeyValue<T extends object>(item: T, rowKey: keyof T | ((item: T) => string)): string {
  if (typeof rowKey === 'function') {
    return rowKey(item);
  }
  return String((item as Record<string, unknown>)[rowKey as string]);
}

/**
 * Structural fix for the `StaffRolesPage`/`OutreachPage` render-loop family (see CLAUDE.md's Test
 * Coverage section): a parent re-deriving `columns` (or anything else feeding `useTableState`)
 * with a fresh reference every render -- unmemoized, by a future page that never gets the memo
 * treatment -- still produces a brand-new `paginatedData` ARRAY every render, even though the ROW
 * OBJECTS inside it are identical. Comparing row-by-row here, not the array's own identity, is
 * what actually breaks that cycle: `onPageDataChange` only fires when a row is genuinely a
 * different object or has genuinely different own-property values, never merely because the array
 * wrapping it is new. Per-page `useMemo`/`useCallback` discipline is still good practice (it avoids
 * needless recompute), but it is no longer the ONLY thing standing between a page and a hang.
 */
function rowsShallowEqual<T>(a: T, b: T): boolean {
  if (a === b) return true;
  if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) return false;
  const aRec = a as Record<string, unknown>;
  const bRec = b as Record<string, unknown>;
  const aKeys = Object.keys(aRec);
  const bKeys = Object.keys(bRec);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => Object.is(aRec[key], bRec[key]));
}

function samePageData<T>(prev: T[] | null, next: T[]): boolean {
  if (prev === null) return false;
  if (prev === next) return true;
  if (prev.length !== next.length) return false;
  return prev.every((item, i) => rowsShallowEqual(item, next[i]));
}

// Dev/test-only circuit breaker: converts a render loop into a loud, fast test failure instead of
// a hang that balloons memory (measured once, by hand, at 1.2+ GB before being killed -- see
// CLAUDE.md). Scoped PER DataTable INSTANCE (not module-level) so a page with several independent
// tables settling normally can't trip each other's count. The reset runs on a microtask, so this
// only fires when the SAME table instance's onPageDataChange effect re-fires faster than anything
// else gets a chance to run in between -- the signature of a tight, self-sustaining loop, not of
// ordinary settling (observed to settle within ~10-15 firings even on a complex page).
const ON_PAGE_DATA_CHANGE_LOOP_THRESHOLD = 30;

/**
 * §16.5: "the highlight marks the first match in each matched field." Only applied to a cell with
 * NO custom `render` -- a column that supplies its own render function returns an opaque
 * ReactNode, and there is no general way to find-and-wrap a substring inside arbitrary JSX without
 * every page's own column definitions cooperating (out of scope here; named so the gap is visible
 * rather than silently incomplete).
 */
function renderWithHighlight(text: string, searchText: string, isSearchedColumn: boolean) {
  if (!isSearchedColumn || !searchText) return text;
  const match = findFirstMatch(text, searchText);
  if (!match) return text;
  return (
    <>
      {text.slice(0, match.start)}
      <mark>{text.slice(match.start, match.end)}</mark>
      {text.slice(match.end)}
    </>
  );
}

export function DataTable<T extends object>({
  data,
  columns,
  searchable = false,
  searchPlaceholder = 'Search...',
  searchKeys = [],
  searchText: controlledSearchText,
  onSearchTextChange,
  sortColumn: controlledSortColumn,
  sortDirection: controlledSortDirection,
  onSortChange,
  renderHeaderExtra,
  onRowClick,
  getRowHref,
  emptyMessage = 'No data found.',
  loading = false,
  rowKey,
  initialSort,
  paginated = false,
  pageSize: controlledPageSize,
  onPageSizeChange,
  currentPage: controlledCurrentPage,
  onPageChange,
  onPageDataChange,
  visibleColumns: visibleColumnsProp,
  groupBy,
  groupGranularity,
}: DataTableProps<T>) {
  // M8: group expansion is presentation and is NOT restored across navigation -- plain component
  // state, reset (all expanded) on every remount. Keyed by bucket KEY, not column, since only one
  // column groups at a time (V1, §13.5).
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const toggleGroupCollapsed = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  // Internal pagination state (used when not controlled)
  const [internalPageSize, setInternalPageSize] = useState<PageSize>(25);
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);

  // Use controlled values if provided, otherwise internal state
  const pageSize = controlledPageSize ?? internalPageSize;
  const currentPage = controlledCurrentPage ?? internalCurrentPage;

  const handlePageSizeChange = (size: PageSize) => {
    if (onPageSizeChange) {
      onPageSizeChange(size);
    } else {
      setInternalPageSize(size);
    }
    // Reset to page 1 when page size changes
    if (onPageChange) {
      onPageChange(1);
    } else {
      setInternalCurrentPage(1);
    }
  };

  const handlePageChange = useCallback((page: number) => {
    if (onPageChange) {
      onPageChange(page);
    } else {
      setInternalCurrentPage(page);
    }
  }, [onPageChange]);

  const displayColumns = useMemo(
    () =>
      visibleColumnsProp
        ? columns.filter(col => visibleColumnsProp.includes(String(col.key)))
        : columns,
    [columns, visibleColumnsProp]
  );

  const columnConfigs = useMemo(
    () =>
      columns.map(col => ({
        key: col.key,
        getValue: col.getValue,
        sortable: col.sortable,
      })),
    [columns]
  );

  const sortControlled = onSortChange !== undefined;
  const searchControlled = onSearchTextChange !== undefined;

  const {
    sortColumn,
    sortDirection,
    searchText,
    setSort,
    setSearchText,
    processedData,
  } = useTableState(data, {
    searchKeys,
    initialSort,
    columns: columnConfigs,
    sort: sortControlled
      ? (controlledSortColumn ? { column: controlledSortColumn, direction: controlledSortDirection ?? 'asc' } : null)
      : undefined,
    onSortChange: sortControlled
      ? (next) => onSortChange!(next as { column: keyof T | string; direction: SortDirection } | null)
      : undefined,
    searchTextValue: searchControlled ? (controlledSearchText ?? '') : undefined,
    onSearchTextChange: searchControlled ? onSearchTextChange : undefined,
  });

  // Group-by disables pagination -- every row renders, organized into sections (see the prop's own
  // doc comment on why: the contract doesn't address the interaction, and a group cut off mid-page
  // is worse than showing it whole).
  const isGrouped = !!groupBy && !!groupGranularity;
  const effectivePaginated = paginated && !isGrouped;

  // Calculate pagination
  const totalItems = processedData.length;
  const totalPages = effectivePaginated ? Math.ceil(totalItems / pageSize) : 1;
  const startIndex = effectivePaginated ? (currentPage - 1) * pageSize : 0;
  const endIndex = effectivePaginated ? Math.min(startIndex + pageSize, totalItems) : totalItems;
  // Memoized so the ref is stable when nothing changed — a fresh slice() every render would make
  // the onPageDataChange effect below fire on every render, and if the parent stores the result in
  // state (select-all-on-page) that becomes an infinite render loop.
  const paginatedData = useMemo(
    () => (effectivePaginated ? processedData.slice(startIndex, endIndex) : processedData),
    [effectivePaginated, processedData, startIndex, endIndex]
  );

  // Fleet filter contract v3 §13.3 (M6): a value that is null, empty or unparseable buckets into
  // "(blank)" -- never dropped. Rows within each bucket keep `paginatedData`'s own order, which is
  // already the active sort (M7: grouping arranges, it does not replace sorting).
  const rowGroups = useMemo(() => {
    if (!isGrouped) return null;
    const groupColumn = columns.find((c) => c.key === groupBy);
    const buckets = new Map<string, { label: string; items: T[] }>();
    for (const item of paginatedData) {
      const raw = groupColumn?.getValue
        ? groupColumn.getValue(item)
        : ((item as Record<string, unknown>)[groupBy as string] as string | number | Date | null | undefined);
      // A boolean getValue (a column that's sortable but not date-shaped) has no meaningful
      // bucket -- treated the same as null/unparseable (M6's "(blank)" sentinel), never a crash.
      const bucket = bucketFor(typeof raw === 'boolean' ? null : raw, groupGranularity!);
      const existing = buckets.get(bucket.key);
      if (existing) existing.items.push(item);
      else buckets.set(bucket.key, { label: bucket.label, items: [item] });
    }
    return Array.from(buckets.entries())
      .sort(([a], [b]) => compareGroupKeys(a, b))
      .map(([key, v]) => ({ key, label: v.label, items: v.items }));
  }, [isGrouped, paginatedData, columns, groupBy, groupGranularity]);

  // Notify parent of page data changes (for select all on page functionality). Structural fix
  // (see rowsShallowEqual/samePageData above): only fires when the ROWS actually changed, never
  // merely because `paginatedData` got a new array reference from an upstream unmemoized `columns`
  // (or anything else `useTableState` depends on). This is what makes the fix hold regardless of
  // whether a given page remembers to memoize its own columns/callbacks.
  const lastSentPageDataRef = useRef<T[] | null>(null);
  const loopGuardCountRef = useRef(0);
  const loopGuardResetScheduledRef = useRef(false);
  useEffect(() => {
    if (!onPageDataChange) return;
    if (samePageData(lastSentPageDataRef.current, paginatedData)) return;
    lastSentPageDataRef.current = paginatedData;

    if (import.meta.env.DEV) {
      loopGuardCountRef.current++;
      if (!loopGuardResetScheduledRef.current) {
        loopGuardResetScheduledRef.current = true;
        Promise.resolve().then(() => {
          loopGuardCountRef.current = 0;
          loopGuardResetScheduledRef.current = false;
        });
      }
      if (loopGuardCountRef.current > ON_PAGE_DATA_CHANGE_LOOP_THRESHOLD) {
        throw new Error(
          `DataTable: onPageDataChange fired ${loopGuardCountRef.current} times for one table ` +
          'instance within a single tick -- this is the StaffRolesPage/OutreachPage render-loop ' +
          'signature (see CLAUDE.md), not ordinary settling. Check for a value feeding `columns` ' +
          '(or another useTableState input) that gets a new identity every render.'
        );
      }
    }

    onPageDataChange(paginatedData);
  }, [paginatedData, onPageDataChange]);

  // Reset to page 1 if current page exceeds total pages (e.g., after filtering)
  useEffect(() => {
    if (paginated && currentPage > totalPages && totalPages > 0) {
      handlePageChange(1);
    }
  }, [totalPages, currentPage, paginated, handlePageChange]);

  const renderSortIcon = (column: Column<T>) => {
    if (!column.sortable) return null;

    const isActive = sortColumn === column.key;

    if (!isActive) {
      return (
        <span className="sort-icon sort-icon-inactive">
          <ArrowUpRegular fontSize={12} />
        </span>
      );
    }

    return (
      <span className="sort-icon">
        {sortDirection === 'asc' ? (
          <ArrowUpRegular fontSize={12} />
        ) : (
          <ArrowDownRegular fontSize={12} />
        )}
      </span>
    );
  };

  const handleHeaderClick = (column: Column<T>) => {
    if (column.sortable) {
      setSort(column.key);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Spinner size="medium" label="Loading..." />
      </div>
    );
  }

  const renderRow = (item: T) => {
    const rowHref = getRowHref?.(item);
    return (
      <TableRow
        key={getRowKeyValue(item, rowKey)}
        className={rowHref ? ROW_LINK_HOST : undefined}
        // A row with a real href gets its click handling from the RowLink rendered in
        // its primary column (see getRowHref doc above) -- attaching a second onClick
        // to the <tr> itself would double up on a plain click and would ignore
        // Ctrl/⌘/middle-click the way the anchor doesn't.
        onClick={!rowHref && onRowClick ? () => onRowClick(item) : undefined}
        style={{ cursor: !rowHref && onRowClick ? 'pointer' : 'default' }}
      >
        {displayColumns.map(column => {
          if (column.render) {
            return <TableCell key={String(column.key)}>{column.render(item)}</TableCell>;
          }
          const raw = String((item as Record<string, unknown>)[column.key as string] ?? '-');
          return (
            <TableCell key={String(column.key)}>
              {renderWithHighlight(raw, searchText, searchKeys.includes(column.key as keyof T))}
            </TableCell>
          );
        })}
      </TableRow>
    );
  };

  return (
    <div className="data-table-container">
      {searchable && !searchControlled && (
        <div className="data-table-search">
          <Input
            placeholder={searchPlaceholder}
            value={searchText}
            onChange={(_, data) => setSearchText(data.value)}
            contentBefore={<SearchRegular />}
            style={{ minWidth: '280px' }}
          />
        </div>
      )}

      {processedData.length > 0 ? (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                {displayColumns.map(column => (
                  <TableHeaderCell
                    key={String(column.key)}
                    className={column.sortable ? 'sortable-header' : ''}
                    onClick={() => handleHeaderClick(column)}
                    style={{
                      width: column.width,
                      cursor: column.sortable ? 'pointer' : 'default',
                    }}
                  >
                    <span className="header-content">
                      {column.header}
                      {renderSortIcon(column)}
                      {renderHeaderExtra?.(String(column.key))}
                    </span>
                  </TableHeaderCell>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rowGroups
                ? rowGroups.flatMap((group) => {
                    const collapsed = collapsedGroups.has(group.key);
                    const header = (
                      <TableRow
                        key={`group-${group.key}`}
                        className="data-table-group-header"
                        onClick={() => toggleGroupCollapsed(group.key)}
                        style={{ cursor: 'pointer', background: tokens.colorNeutralBackground2 }}
                      >
                        <TableCell colSpan={displayColumns.length}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}>
                            {collapsed ? <ChevronRight12Regular /> : <ChevronDown12Regular />}
                            {group.label}
                            <Text size={200} style={{ color: tokens.colorNeutralForeground3, fontWeight: 400 }}>
                              ({group.items.length})
                            </Text>
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                    return collapsed ? [header] : [header, ...group.items.map(renderRow)];
                  })
                : paginatedData.map(renderRow)}
            </TableBody>
          </Table>

          {/* Pagination Controls */}
          {paginated && totalPages > 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 0',
                marginTop: '8px',
                borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                  Showing {startIndex + 1}-{endIndex} of {totalItems}
                </Text>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                    Per page:
                  </Text>
                  <Dropdown
                    value={pageSize.toString()}
                    onOptionSelect={(_, data) => handlePageSizeChange(parseInt(data.optionValue || '25') as PageSize)}
                    style={{ minWidth: '80px' }}
                    size="small"
                  >
                    {PAGE_SIZE_OPTIONS.map(size => (
                      <Option key={size} value={size.toString()} text={size.toString()}>
                        {size}
                      </Option>
                    ))}
                  </Dropdown>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Button
                    appearance="subtle"
                    icon={<ChevronDoubleLeftRegular />}
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    size="small"
                    title="First page"
                  />
                  <Button
                    appearance="subtle"
                    icon={<ChevronLeftRegular />}
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    size="small"
                    title="Previous page"
                  />
                  <Text size={200} style={{ padding: '0 8px', minWidth: '80px', textAlign: 'center' }}>
                    Page {currentPage} of {totalPages}
                  </Text>
                  <Button
                    appearance="subtle"
                    icon={<ChevronRightRegular />}
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    size="small"
                    title="Next page"
                  />
                  <Button
                    appearance="subtle"
                    icon={<ChevronDoubleRightRegular />}
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage >= totalPages}
                    size="small"
                    title="Last page"
                  />
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="empty-state">
          <p>{emptyMessage}</p>
        </div>
      )}
    </div>
  );
}

export default DataTable;
